import dagre from "@dagrejs/dagre";
import { MarkerType } from "reactflow";
import { PHASES } from "./workflowConstants.js";

export const STATE_WIDTH = 230;
export const NODE_HEIGHT = 36;
const FORWARD_EDGE_COLOR = "#cfcfcf";
export const BACK_EDGE_COLOR = "#e53935";

const REOPEN_PHASE = "abgeschlossen";
const REOPEN_TARGET = "pre_check";
const GROUP_PADDING = { top: 36, side: 20, bottom: 20 };

/**
 * Flussdiagramm: Status in einer vertikalen Kette, gruppiert in Phasen-Rahmen.
 * Rücksprünge laufen rot an der linken Seite. Das Wiedereröffnen aus
 * "Abgeschlossen" wird nur als Hinweis im Rahmen gezeigt, nicht als Linien.
 * Aktionen und Erinnerungen zeigt nur die Statusliste.
 */
export function buildDiagram(states, transitions) {
  const phase_of = (value) =>
    PHASES.find((phase) => phase.states.includes(value))?.id;

  const is_reopen = (t) =>
    phase_of(t.from) === REOPEN_PHASE && t.to === REOPEN_TARGET;
  const drawn = transitions.filter((t) => !is_reopen(t));
  const reopen_hidden = drawn.length < transitions.length;

  const graph = new dagre.graphlib.Graph({ compound: true });
  graph.setGraph({ rankdir: "TB", nodesep: 70, ranksep: 25 });
  graph.setDefaultEdgeLabel(() => ({}));

  PHASES.forEach((phase) => graph.setNode(phase.id, {}));
  states.forEach((state) => {
    graph.setNode(state.value, { width: STATE_WIDTH, height: NODE_HEIGHT });
    if (phase_of(state.value))
      graph.setParent(state.value, phase_of(state.value));
  });
  drawn.forEach((t) => graph.setEdge(t.from, t.to));
  dagre.layout(graph);

  // Rahmen aus den Maßen der enthaltenen Knoten statt aus dem Cluster von dagre:
  // dessen Rand wird nicht um das Padding erweitert, benachbarte Rahmen würden sich
  // überlappen.
  const frames = PHASES.map((phase) => {
    const members = states
      .filter((state) => phase_of(state.value) === phase.id)
      .map((state) => {
        const { x, y } = graph.node(state.value);
        return {
          left: x - STATE_WIDTH / 2,
          right: x + STATE_WIDTH / 2,
          top: y - NODE_HEIGHT / 2,
          bottom: y + NODE_HEIGHT / 2,
        };
      });
    const left = Math.min(...members.map((m) => m.left));
    const right = Math.max(...members.map((m) => m.right));
    const top = Math.min(...members.map((m) => m.top));
    const bottom = Math.max(...members.map((m) => m.bottom));
    return {
      phase,
      left: left - GROUP_PADDING.side,
      top: top - GROUP_PADDING.top,
      width: right - left + 2 * GROUP_PADDING.side,
      height: bottom - top + GROUP_PADDING.top + GROUP_PADDING.bottom,
    };
  });

  const place = (value) => {
    const { x, y } = graph.node(value);
    const phase_id = phase_of(value);
    const frame = frames.find((f) => f.phase.id === phase_id);
    const origin = { x: frame?.left ?? 0, y: frame?.top ?? 0 };
    return {
      position: {
        x: x - STATE_WIDTH / 2 - origin.x,
        y: y - NODE_HEIGHT / 2 - origin.y,
      },
      ...(frame && { parentNode: phase_id }),
    };
  };

  const nodes = [
    ...frames.map(({ phase, left, top, width, height }) => ({
      id: phase.id,
      type: "phase",
      data: {
        label: phase.label,
        note:
          reopen_hidden && phase.id === REOPEN_PHASE
            ? "↺ Wiedereröffnen → In Prüfung"
            : undefined,
      },
      position: { x: left, y: top },
      style: { width, height },
      selectable: false,
      draggable: false,
      zIndex: -1,
    })),
    ...states.map((state) => ({
      id: state.value,
      type: "state",
      data: { value: state.value, label: state.label },
      ...place(state.value),
    })),
  ];

  const edges = [];
  let back_index = 0;
  drawn.forEach((t) => {
    const is_back = graph.node(t.to).y <= graph.node(t.from).y;

    if (is_back) {
      edges.push({
        id: `${t.from}>${t.to}`,
        type: "smoothstep",
        source: t.from,
        sourceHandle: "back-out",
        target: t.to,
        targetHandle: "back-in",
        pathOptions: { borderRadius: 0, offset: 25 + back_index * 14 },
        markerEnd: { type: MarkerType.ArrowClosed, color: BACK_EDGE_COLOR },
        style: { stroke: BACK_EDGE_COLOR, strokeWidth: 1.5 },
      });
      back_index += 1;
    } else {
      edges.push({
        id: `${t.from}>${t.to}`,
        source: t.from,
        target: t.to,
        type: "smoothstep",
        pathOptions: { borderRadius: 0 },
        markerEnd: { type: MarkerType.ArrowClosed, color: FORWARD_EDGE_COLOR },
        style: { stroke: FORWARD_EDGE_COLOR, strokeWidth: 6 },
      });
    }
  });

  return { nodes, edges };
}
